import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate } from 'react-router';
import { Alert, Badge, Button, Card, Field, Input } from '../../components/ui';
import { api, ApiError } from '../../lib/api';
import { useSession } from '../../lib/session';
import type { Product } from '../../lib/types';

export function ProductsPage() {
  const { t } = useTranslation();
  const { identity } = useSession();
  const orgId = identity?.org?.id;
  // Support mode (Section 16.3): product folders live in the org's Team Space -> not creatable cross-org
  const canCreate =
    !identity?.supportMode &&
    (identity?.roles.some((r) => ['admin', 'ra', 'design'].includes(r)) || identity?.superAdmin);
  const [products, setProducts] = useState<Product[] | null>(null);
  const [open, setOpen] = useState(false);
  const localToday = () => new Intl.DateTimeFormat('en-CA').format(new Date());
  const [f, setF] = useState({ name: '', description: '', startDate: localToday() });
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const load = () =>
    orgId &&
    api<{ products: Product[] }>(`/api/v1/orgs/${orgId}/products`).then((r) => setProducts(r.products));
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);
  if (identity && identity.state !== 'member') return <Navigate to="/" replace />;

  const create = async () => {
    setBusy(true);
    setErr(null);
    try {
      await api(`/api/v1/orgs/${orgId}/products`, { method: 'POST', json: f });
      setOpen(false);
      setF({ name: '', description: '', startDate: localToday() });
      await load();
    } catch (e) {
      setErr(
        e instanceof ApiError
          ? t([`products.err_${e.code}`, 'products.err_generic'], { code: e.code }) + ` ${e.message}`
          : String(e),
      );
    } finally {
      setBusy(false);
    }
  };
  const counts = (p: Product) => Object.entries(p.documentCounts).filter(([, n]) => n > 0);
  const verified = (p: Product) => {
    const c = p.documentCounts;
    const total = Object.values(c).reduce((a, n) => a + n, 0);
    return { done: (c.pass ?? 0) + (c.not_compliant ?? 0), total };
  };
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <h1 className="text-2xl font-semibold">{t('products.title')}</h1>
        {canCreate && (
          <Button className="ml-auto" onClick={() => setOpen((v) => !v)}>
            {t('products.new')}
          </Button>
        )}
      </div>
      {open && (
        <Card title={t('products.new')}>
          {err && <Alert>{err}</Alert>}
          <div className="grid gap-3 md:grid-cols-2">
            <Field label={t('products.name')}>
              <Input
                value={f.name}
                onChange={(e) => setF({ ...f, name: e.target.value })}
                placeholder="Vitamin C 1000 Stick"
              />
            </Field>
            <Field label={t('products.description')}>
              <Input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
            </Field>
          </div>
          <div className="mt-3 flex gap-2">
            <Button onClick={() => void create()} disabled={busy || f.name.trim().length < 2}>
              {t('products.create')}
            </Button>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              {t('products.cancel')}
            </Button>
          </div>
        </Card>
      )}
      <Card>
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="py-2">{t('products.id')}</th>
              <th>{t('products.name')}</th>
              <th>{t('products.status')}</th>
              <th>{t('products.verified')}</th>
              <th>{t('products.docs')}</th>
              <th>{t('products.created')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {products?.map((p) => (
              <tr key={p.id} className={p.status === 'archived' ? 'opacity-60' : ''}>
                <td className="py-2 font-mono text-xs">
                  <Link className="text-emerald-700 underline" to={`/products/${p.id}`}>
                    {p.id}
                  </Link>
                </td>
                <td>{p.name}</td>
                <td>
                  <Badge tone={p.status === 'active' ? 'green' : 'slate'}>{t(`products.${p.status}`)}</Badge>
                </td>
                <td className="text-xs text-slate-600">
                  {verified(p).total ? (
                    <span>
                      <span className="font-semibold">{verified(p).done}</span> / {verified(p).total}
                    </span>
                  ) : (
                    <span className="text-slate-400">-</span>
                  )}
                </td>
                <td className="flex gap-1 py-2">
                  {counts(p).length === 0 && <span className="text-slate-400">-</span>}
                  {counts(p).map(([s, n]) => (
                    <Badge
                      key={s}
                      tone={
                        s === 'pass'
                          ? 'green'
                          : s === 'not_compliant' || s === 'failed'
                            ? 'red'
                            : s === 'verifying'
                              ? 'blue'
                              : 'slate'
                      }
                    >
                      {n} {t(`docs.status_${s}`)}
                    </Badge>
                  ))}
                  {!!p.documentExcluded && (
                    <span className="text-xs text-slate-500" title={t('docs.excludeHint')}>
                      {t('docs.excludedCount', { count: p.documentExcluded })}
                    </span>
                  )}
                </td>
                <td>{p.createdAt.slice(0, 10)}</td>
              </tr>
            ))}
            {products?.length === 0 && (
              <tr>
                <td colSpan={6} className="py-6 text-center text-slate-500">
                  {t('products.empty')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
